<template>
    <div class="container" v-if="invoice && customer" :key="invoice.id">
        <v-title :breadcrumb="breadcrumb">
            Invoice {{invoice.invoiceNumberLabel}}
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <div class="tile position-relative">
                            <v-inline-loader v-if="isLoading"/>
                            <div class="tile-content" v-else>
                                <invoice-context
                                    :invoice="invoice"
                                    @update-invoice-field="updateInvoiceField"
                                />
                                <invoice-items
                                    @update-autopay-and-retry="updateAutopayAndRetry"
                                    @update-invoice-field="updateInvoiceField"
                                    :invoice="invoice"
                                    ref="invoiceItems">
                                    <template slot="transactionItems">
                                        <tr v-for="transaction in allTransactionItems" :key="transaction.id">
                                            <td>
                                                <router-link class="inline-s" :to="{name:'transaction-details', params: {id: transaction.id}}">
                                                    {{transaction.explanation}}
                                                </router-link>
                                                <r-badge
                                                    v-if="transaction.isDisputed"
                                                    type="warning">Disputed</r-badge>
                                            </td>
                                            <td class="text-right is-numeric" :class="{'line-through': transaction.isCaptured}">
                                                {{transaction.signedAmount | cover(!transaction.isApproved) | monetize(transaction.currency)}}
                                            </td>
                                        </tr>
                                    </template>
                                    <template slot="creditMemoItems">
                                        <tr v-for="creditMemo in creditMemos">
                                            <td>
                                                <router-link class="inline-s" :to="{name:'credit-memo-details', params: {id: creditMemo.id}}">
                                                    Credit Memo {{creditMemo.numberLabel}}
                                                </router-link>
                                            </td>
                                            <td class="text-right is-numeric">
                                                {{creditMemo.getAmountAllocatedToInvoice(invoice.id) * -1 | monetize(creditMemo.currency)}}
                                            </td>
                                        </tr>
                                    </template>
                                    <template slot="amountDue">
                                        <tr>
                                            <td><strong>Amount Due</strong></td>
                                            <td class="text-right is-numeric">{{invoice.amountDue | monetize(invoice.currency)}}</td>
                                        </tr>
                                    </template>
                                </invoice-items>
                            </div>
                            <div class="tile-content" v-if="invoice.isPayable">
                                <div class="grid-content align-items-center">
                                    <div class="grid-item text-left">
                                        <v-icon icon="transactions" class="inline-s"/><h3 class="display-inline">Make full or partial payment</h3>
                                    </div>
                                    <div class="grid-item text-right">
                                        <r-button type="primary" :loading="isProcessingPayment" @click="startPayment">Pay Invoice</r-button>
                                    </div>
                                </div>
                                <payment-modal
                                    v-if="isPaying"
                                    :customer="customer"
                                    :invoice="invoice"
                                    :amount-owed="invoice.amountDue"
                                    @close="stopPayment"
                                    @pay-invoice="payInvoice"
                                />
                            </div>
                            <template v-for="transaction in invoice.refundableTransactions">
                                <div class="tile-content" v-if="isRefundableTransaction(transaction)">
                                    <div class="grid-content align-items-center">
                                        <div class="grid-item text-left">
                                            <v-icon icon="transactions" class="inline-s"/><h3 class="display-inline">Payment for invoice {{refundableAmount(transaction) | monetizeExplicit(transaction.currency)}}</h3>
                                        </div>
                                        <div class="grid-item text-right">
                                            <r-button type="primary" :loading="isProcessingRefund" @click="startRefund(transaction)">Refund</r-button>
                                        </div>
                                    </div>
                                    <refund-modal
                                        v-if="currentRefundTransactionId === transaction.id"
                                        :invoice="invoice"
                                        :max-refundable-amount="maxRefundableAmount"
                                        @refund="refundTransaction"
                                        @close="stopRefunding"
                                    />
                                </div>
                            </template>
                            <div class="tile-content">
                                <div class="grid-content">
                                    <div class="grid-item grid-item-third" v-if="invoice.isVoidable">
                                        <r-button
                                            fluid
                                            class="inline-s"
                                            :loading="isVoidingInvoice"
                                            @click="isConfirmingVoid = true"
                                        >
                                            Void
                                        </r-button>
                                        <void-invoice-modal
                                            v-if="isConfirmingVoid"
                                            @void-invoice="voidInvoice"
                                            @close="isConfirmingVoid = false"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-third" v-if="invoice.isAbandonable">
                                        <r-button
                                            fluid
                                            class="inline-s"
                                            :loading="isAbandoningInvoice"
                                            @click="isConfirmingAbandon = true"
                                        >
                                            Abandon
                                        </r-button>
                                        <abandon-invoice-modal
                                            v-if="isConfirmingAbandon"
                                            @abandon-invoice="abandonInvoice"
                                            @close="isConfirmingAbandon = false"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-third" v-if="invoice.isIssuable">
                                        <r-button
                                            fluid
                                            class="inline-s"
                                            :loading="isIssuingInvoice"
                                            @click="isSelectingIssueTime = true"
                                        >
                                            Issue
                                        </r-button>
                                        <issue-invoice-modal
                                            v-if="isSelectingIssueTime"
                                            @issue-invoice="issueInvoice"
                                            @close="isSelectingIssueTime = false"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-third" v-if="invoice.isReissuable">
                                        <r-button
                                            fluid
                                            class="inline-s"
                                            :loading="isReissuingInvoice"
                                            @click="isConfirmingReissue = true"
                                        >
                                             {{invoice.status === 'draft' ? 'Issue':'Reissue'}}
                                        </r-button>
                                        <reissue-invoice-modal
                                            v-if="isConfirmingReissue"
                                            :invoice-status="invoice.status"
                                            @reissue-invoice="reissueInvoice"
                                            @close="isConfirmingReissue = false"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-third">
                                        <r-button
                                            fluid
                                            class="inline-s"
                                            :loading="isDownloading"
                                            @click="downloadPDF">
                                            Download Invoice
                                        </r-button>
                                    </div>
                                    <div class="grid-item grid-item-third">
                                        <r-button
                                            fluid
                                            :disabled="isDraftingEmail"
                                            :loading="isSendingEmail"
                                            @click="draftEmail">
                                            Email Invoice
                                        </r-button>
                                        <email-modal
                                            v-if="isDraftingEmail"
                                            :customer="customer"
                                            :invoice="invoice"
                                            :email="draft"
                                            @close="isDraftingEmail = false"
                                            @input="sendEmail"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-third">
                                        <r-button class="has-tooltip-bottom" data-tooltip="Coming Soon" fluid>
                                            Invoice Link
                                        </r-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full">
                        <timeline
                            ref="timeline"
                            type="invoice"
                            :key="`customer-${customer.id}-invoice-${invoice.id}-timeline`"
                            :entity="invoice"/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="grid">
                    <div class="grid-item grid-item-full" v-if="$auth.subscriptions.read.isAllowed && invoice.hasSubscription">
                        <order-details-tile
                            :order="order"
                            :customer="customer"
                            :summary="summary"
                            :is-loading="isLoading"
                            :orders-custom-fields-schema="ordersCustomFieldsSchema"
                            @refresh="fetchData({updateTimeline: true})"
                        />
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.invoices.read.isAllowed && invoice.hasSubscription">
                        <div class="tile position-relative">
                            <div class="tile-title">
                                <slot name="title">
                                    <h2>Upcoming Invoices for this Order</h2>
                                </slot>
                            </div>
                            <upcoming-invoices :order="order"/>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.invoices.read.isAllowed && invoice.hasSubscription">
                        <div class="tile position-relative">
                            <div class="tile-title">
                                <slot name="title">
                                    <h2>Invoices for this Order</h2>
                                </slot>
                            </div>
                            <invoices
                                :invoices-params="{
                                    sort: `-issuedTime`,
                                    filter: `subscriptionId:${invoice.subscriptionId};id:!${invoice.id}`,
                                }">
                                <template slot="headers">
                                    <th>Invoice<br/>Number</th>
                                    <th>Date<br/>Issued</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                </template>
                                <template slot="iteratee" slot-scope="{item}">
                                    <td v-fs-block>
                                        <router-link :to="{name: 'invoice-details', params: {id: item.id}}">
                                            {{item.invoiceNumberLabel}}
                                        </router-link>
                                    </td>
                                    <td class="no-wrap" v-fs-block>{{item.issuedTime | dateUTC('shortDate')}}</td>
                                    <td class="no-wrap" v-fs-block>{{item.amount | monetize(item.currency)}}</td>
                                    <td v-fs-block>
                                        <r-badge :type="getBadgeType('invoiceStatus', item.status)">
                                            {{item.statusLabel}}
                                        </r-badge>
                                    </td>
                                </template>
                                <template slot="no-content">
                                    <p class="text-muted">This order has no other invoices</p>
                                </template>
                            </invoices>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full">
                        <risk-analysis :model="order"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import vContact from '@/components/v-contact';
    import vInlineLoader from '@/components/v-inline-loader';
    import vTitle from '@/components/v-title';
    import {getBadgeType} from '@/data/badges-color-map';
    import {vIcon} from '@/components/form';
    import emailMessage from './email-message';
    import Timeline from '@/components/timeline/timeline';
    import compare from '@/common/helpers/compare';
    import unCamelCase from '@/common/helpers/un-camel-case';

    import FormLoaderMixin from '@/mixins/form-loader-mixin';

    import CreditMemoModel from '@/common/models/credit-memo-model';
    import CustomerModel from '@/common/models/customer-model';
    import EmailMessageModel from '@/common/models/email-message-model';
    import InvoiceModel from '@/common/models/invoice-model';
    import InvoiceTransactionModel from '@/common/models/invoice-transaction-model';
    import Media from '@/common/files/media';
    import OrderModel from '@/common/models/order-model';
    import OrderSummaryModel from '@/common/models/order-summary-model';
    import TransactionModel from '@/common/models/transaction-model';

    import InvoiceContext from '@/modules/invoices/invoice-context';
    import InvoiceItems from '@/modules/invoices/invoice-items';
    import RiskAnalysis from '@/components/risk-analysis/risk-analysis';
    import Invoices from '@/modules/invoices/invoices-list';
    import UpcomingInvoices from '@/modules/invoices/invoices-list-upcoming';
    import OrderDetailsTile from '@/modules/invoices/components/order-details-tile';

    export default {
        name: 'invoice-details',
        components: {
            vContact,
            vIcon,
            vInlineLoader,
            vTitle,
            Timeline,
            InvoiceContext,
            InvoiceItems,
            RiskAnalysis,
            Invoices,
            UpcomingInvoices,
            OrderDetailsTile,
            EmailModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-email-modal'),
            RefundModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-refund'),
            PaymentModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-payment'),
            VoidInvoiceModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-void-modal'),
            AbandonInvoiceModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-abandon-modal'),
            IssueInvoiceModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-issue-modal'),
            ReissueInvoiceModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/invoice-reissue-modal'),
        },
        mixins: [FormLoaderMixin],
        data() {
            return {
                isPaying: false,
                isLoading: false,
                isSendingEmail: false,
                isDraftingEmail: false,
                isDownloading: false,
                isProcessingRefund: false,
                isProcessingPayment: false,
                isConfirmingVoid: false,
                isConfirmingReissue: false,
                isVoidingInvoice: false,
                isReissuingInvoice: false,
                isConfirmingAbandon: false,
                isAbandoningInvoice: false,
                isSelectingIssueTime: false,
                isIssuingInvoice: false,
                customer: null,
                invoice: null,
                order: new OrderModel(),
                ordersCustomFieldsSchema: [],
                summary: null,
                currentRefundTransactionId: null,
                refundTransactionItems: [],
                refundTransactionData: null,
                maxRefundableAmount: null,
                creditMemos: [],
                reloadOnParam: 'id',
            };
        },
        computed: {
            breadcrumb() {
                return {
                    label: 'Invoice',
                    route: {
                        name: 'invoice-details',
                        params: {
                            id: this.id,
                        },
                    },
                };
            },
            allTransactionItems() {
                return [...this.invoice.transactions, ...this.refundTransactionItems]
                    .filter(transaction => transaction.affectsCurrentInvoice)
                    .sort((trX, trY) => compare.dates(trX.processedTime, trY.processedTime));
            },
        },
        methods: {
            getBadgeType,
            async fetchData({updateTimeline = false} = {}) {
                this.isLoading = true;
                try {
                    const invoiceParams = {
                        id: this.id,
                        expand: 'organization,items.*.product,items.*.plan',
                    };
                    const {fields: invoiceFields} = await this.$api(({invoices}) => invoices.get(invoiceParams));
                    this.invoice = new InvoiceModel(invoiceFields);

                    const [
                        {fields: customerFields},
                        {items: creditMemoItems},
                        {items: invoiceTransactionItems},
                    ] = await Promise.all([
                        this.$api(({customers}) => customers.get({
                            id: this.invoice.customerId,
                            expand: 'defaultPaymentInstrument',
                        })),
                        this.$api(({invoices}) => invoices.getAllCreditMemos({id: this.invoice.id})),
                        this.$api(({invoices}) => invoices.getAllTransactionAllocations({id: this.invoice.id})),
                    ]);
                    this.customer = new CustomerModel(customerFields);
                    this.creditMemos = creditMemoItems.map(({fields}) => new CreditMemoModel(fields));
                    const invoiceTransactions = invoiceTransactionItems.map(({fields}) => new InvoiceTransactionModel(fields));

                    if (this.invoice.hasSubscription) {
                        // Allow for ElasticSearch to update before fetching connected resource
                        setTimeout(async () => {
                            const orderParams = {
                                id: this.invoice.subscriptionId,
                                expand: 'customer',
                            };
                            const orderSummaryParams = {
                                subscriptionId: this.invoice.subscriptionId,
                            };
                            const [
                                {fields: orderFields},
                                {fields: orderSummaryFields},
                            ] = await Promise.all([
                                this.$api(({subscriptions}) => subscriptions.get(orderParams)),
                                this.$api(({subscriptions}) => subscriptions.getSubscriptionSummaryMetrics(orderSummaryParams)),
                            ]);
                            this.order = new OrderModel(orderFields);

                            const {items: ordersCustomFieldsSchema} = await this.$api(({customFields}) => customFields.getAll({resource: 'subscriptions'}));
                            this.ordersCustomFieldsSchema = ordersCustomFieldsSchema;

                            if (orderSummaryFields !== null) {
                                this.summary = new OrderSummaryModel(orderSummaryFields);
                            }
                        }, 1200);
                    }

                    const childTransactionsCollection = await Promise.all(
                        this.invoice.transactions.map((transaction) => {
                            if (transaction.hasChildTransactions) {
                                return this.$api(({transactions}) => transactions.getAll({
                                    filter: `parentTransactionId:${transaction.id}`,
                                }));
                            }
                            return null;
                        }).filter(this.$filters.isNotEmpty),
                    );

                    const childTransactionItems = []
                        .concat(...childTransactionsCollection.map(collection => collection.items))
                        .map(({fields}) => new TransactionModel(fields));

                    this.refundTransactionItems = childTransactionItems.filter(transaction => transaction.isRefundType || transaction.isVoidType);

                    this.invoice.transactions.forEach((transaction) => {
                        const {amount: invoiceAmount = null} = invoiceTransactions.find(item => item.transactionId === transaction.id) || {};
                        transaction.invoiceAmount = invoiceAmount;
                        transaction.refundedTransactions = this.refundTransactionItems.filter(refundTransaction => refundTransaction.parentTransactionId === transaction.id);
                        transaction.captureTransactions = childTransactionItems
                            .filter(childTransaction => childTransaction.isCaptureType)
                            .filter(captureTransaction => captureTransaction.parentTransactionId === transaction.id);
                    });
                    this.refundTransactionItems.forEach((transaction) => {
                        if (transaction.isVoidType) {
                            const {invoiceAmount = null} = this.invoice.transactions.find(parentTransaction => parentTransaction.id === transaction.parentTransactionId) || {};
                            transaction.invoiceAmount = invoiceAmount;
                        }
                    });

                    if (updateTimeline) {
                        this.$refs.timeline.update();
                    }
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isLoading = false;
                }
            },
            async updateAutopayAndRetry(invoice) {
                try {
                    await this.updateInvoice({
                        data: invoice.getPayload({retryInstruction: true}),
                    });
                    this.$toast.positive(`The invoice's autopay schedule has been updated.`);
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to update the autopay schedule at this time. Please try again later or contact support.');
                }
            },
            async updateInvoiceField({field, value}) {
                try {
                    await this.updateInvoice({
                        data: this.invoice.cloneSelf({[field]: value}).getPayload(),
                        reload: false,
                    });
                    this.$toast.positive(`Invoice ${unCamelCase(field)} updated`);
                } catch (error) {
                    this.$toast.fromError(error, `Unable to update the invoice ${unCamelCase(field)} at this time. Please try again later or contact support.`);
                }
            },
            /**
             * Delegate method to update an invoice.
             * @param {object} data invoice payload
             * @param {boolean?} reload whether to reload the UI after the edit,
             * usually only required for timeline updates
             * @returns {Promise<void>}
             */
            async updateInvoice({data, reload = true}) {
                await this.$api(({invoices}) => invoices.update({id: this.invoice.id, data}));
                if (reload) {
                    await this.fetchData({updateTimeline: true});
                }
            },
            async downloadPDF() {
                this.isDownloading = true;
                const {invoice} = this;
                const fileName = `${this.customer.displayName || this.id}-invoice#${invoice.invoiceNumber}`;
                try {
                    const {data} = await this.$api(({invoices}) => invoices.downloadPDF({id: invoice.id}));
                    const media = new Media({
                        mimeType: 'application/pdf',
                        data,
                        fileName,
                    });
                    media.triggerDownload();
                } catch (error) {
                    this.$toast
                        .negative('Unable to download the invoice pdf. Please try again later or contact support.');
                }
                this.isDownloading = false;
            },
            emailInvoiceInfo() {
                const {tzDate} = this.$filters;
                const {invoiceTimeZone} = this.invoice.organization;
                const invoiceItems = this.invoice.items.map(item => ({
                    name: item.productName,
                    description: item.description,
                    quantity: item.quantity,
                    period: item.hasPeriod
                        ? `${tzDate(item.periodStartTime, invoiceTimeZone)} - ${tzDate(item.periodEndTime, invoiceTimeZone)}`
                        : '',
                    unitPrice: this.$filters.monetizeExplicit(item.unitPrice, this.invoice.currency),
                    price: this.$filters.monetizeExplicit((item.quantity * item.unitPrice), this.invoice.currency),
                }));
                const transformTransactionsToArray = (transactions) => {
                    if (transactions.length) {
                        return transactions.map(transaction => ({
                            description: transaction.explanation,
                            amount: this.$filters.cover(
                                this.$filters.monetize(transaction.signedAmount, transaction.currency),
                                !transaction.isApproved,
                            ),
                            strikethrough: transaction.isCaptured,
                        }));
                    }
                    return null;
                };
                const transformCreditMemosToArray = (creditMemos, invoiceId) => {
                    if (creditMemos.length) {
                        return creditMemos.map(creditMemo => ({
                            description: `Credit Memo ${creditMemo.numberLabel}`,
                            amount: this.$filters.monetize(creditMemo.getAmountAllocatedToInvoice(invoiceId) * -1, creditMemo.currency),
                            strikethrough: false,
                        }));
                    }
                    return null;
                };
                const getContactInfo = address => (
                    {
                        name: address.displayName,
                        organization: address.organization,
                        address: address.address,
                        address2: address.address2,
                        cityRegionPostalCode: address.cityRegionPostalCode,
                        country: address.country,
                        email: address.primaryEmailAddress,
                        phoneNumber: address.primaryPhoneNumber,
                    }
                );
                return {
                    invoiceNumber: this.invoice.invoiceNumberLabel,
                    invoiceStatus: this.invoice.statusLabel,
                    invoiceCustomerId: this.invoice.customerId,
                    invoiceId: this.invoice.id,
                    invoicePO: this.invoice.poNumber,
                    invoiceDueDate: tzDate(this.invoice.dueTime, invoiceTimeZone),
                    invoiceIssueDate: tzDate(this.invoice.issuedTime, invoiceTimeZone),
                    billedTo: {
                        ...getContactInfo(this.invoice.billingAddress),
                    },
                    deliveredTo: {
                        ...getContactInfo(this.invoice.deliveryAddress),
                    },
                    invoiceItems,
                    amountDue: this.$filters.monetize(this.invoice.amountDue, this.invoice.currency),
                    transactionItems: transformTransactionsToArray(this.allTransactionItems),
                    creditMemoItems: transformCreditMemosToArray(this.creditMemos, this.invoice.id),
                    ...this.$refs.invoiceItems.getInvoiceBreakdown,
                };
            },
            draftEmail() {
                this.draft = new EmailMessageModel({
                    to: [this.customer.email],
                    from: this.$store.getters['user/email'],
                    subject: `Invoice #${this.invoice.invoiceNumber}`,
                    html: emailMessage(this.emailInvoiceInfo()),
                    text: emailMessage(this.emailInvoiceInfo(), {textOnly: true}),
                    metadata: {
                        customerId: this.customer.id,
                        eventType: 'direct-email-sent',
                    },
                    attachments: [],
                });
                this.isDraftingEmail = true;
            },
            async sendEmail(email) {
                this.isSendingEmail = true;
                const data = email.getPayload();
                try {
                    const message = await this.$api(({emailMessages}) => emailMessages.create({data}));
                    await this.$api(({emailMessages}) => emailMessages.send({id: message.fields.id}));
                    this.$toast.positive('Email has been sent.');
                    this.$emit('send-email');
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                }
                this.isSendingEmail = false;
            },
            async voidInvoice() {
                this.isVoidingInvoice = true;
                try {
                    const {id} = this.invoice;
                    await this.$api(({invoices}) => invoices.void({id}));
                    await this.fetchData({updateTimeline: true});
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isVoidingInvoice = false;
                }
            },
            async abandonInvoice() {
                this.isAbandoningInvoice = true;
                try {
                    const {id} = this.invoice;
                    await this.$api(({invoices}) => invoices.abandon({id}));
                    await this.fetchData({updateTimeline: true});
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isAbandoningInvoice = false;
                }
            },
            async reissueInvoice(data) {
                this.isReissuingInvoice = true;
                try {
                    const {id} = this.invoice;
                    await this.$api(({invoices}) => invoices.reissue({id, data}));
                    await this.fetchData({updateTimeline: true});
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isReissuingInvoice = false;
                }
            },
            async issueInvoice(data) {
                this.isIssuingInvoice = true;
                try {
                    const {id} = this.invoice;
                    await this.$api(({invoices}) => invoices.issue({id, data}));
                    await this.fetchData();
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isIssuingInvoice = false;
                }
            },
            startPayment() {
                this.isPaying = true;
            },
            stopPayment() {
                this.isPaying = false;
            },
            async payInvoice(amountToPay, paymentInstrument, processedTime, payOutside) {
                this.isProcessingPayment = true;
                const data = {
                    customerId: this.customer.id,
                    websiteId: this.invoice.websiteId,
                    paymentInstrument: paymentInstrument.getPaymentInstrumentModelPayload(),
                    invoiceIds: [this.invoice.id],
                    type: 'sale',
                    currency: this.invoice.currency,
                    amount: amountToPay,
                    processedTime,
                    isProcessedOutside: payOutside,
                };
                try {
                    const {fields: transactionFields} = await this.$api(({transactions}) => transactions.create({data}));
                    if ([
                        TransactionModel.Results.abandoned,
                        TransactionModel.Results.canceled,
                        TransactionModel.Results.declined,
                    ].includes(transactionFields.result)) {
                        this.$toast.negative(`Payment has been ${transactionFields.result}, please try again or contact support`);
                    }
                    await this.fetchData({updateTimeline: true});
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to process payment. Please try again or contact customer service');
                } finally {
                    this.isProcessingPayment = false;
                }
            },
            startRefund(transaction) {
                this.currentRefundTransactionId = transaction.id;
                this.maxRefundableAmount = this.refundableAmount(transaction);
            },
            stopRefunding() {
                this.currentRefundTransactionId = null;
                this.maxRefundableAmount = null;
            },
            async refundTransaction(amountToRefund) {
                this.isProcessingRefund = true;
                try {
                    const params = {
                        id: this.currentRefundTransactionId,
                        data: {
                            amount: amountToRefund,
                        },
                    };
                    await this.$api(({transactions}) => transactions.refund(params));
                    await this.fetchData({updateTimeline: true});
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to process refund. Please try again or contact customer service');
                } finally {
                    this.isProcessingRefund = false;
                    this.refundTransactionData = null;
                }
            },
            refundableAmount(transaction) {
                const transactionAmount = parseFloat(transaction.invoiceAmount || transaction.amount);

                if (transaction.refundedTransactions) {
                    const refundAmount = transaction.refundedTransactions
                        .map(item => item.amount)
                        .reduce((accumulator, amount) => accumulator + parseFloat(amount), 0);
                    return transactionAmount - refundAmount;
                }
                return transactionAmount;
            },
            isRefundableTransaction(transaction) {
                return this.refundableAmount(transaction) > 0;
            },
        },
    };
</script>
